import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { useTranslation } from 'react-i18next';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { useDateTimeFormatPreferences } from '@/datetime/useDateTimeFormatPreferences.hook';
import { ITEM_TYPES, JOURNAL_OR_REPORT, UNNAMED_REPORT } from '@/homescreen/homescreen.constants';
import { useHomeScreenSearchText } from '@/homescreen/homescreen.store';
import { isNewHomeScreenWithMeilisearchEnabled } from '@/services/systemConfiguration.utilities';
import {
  formatJournalReportContent,
  getItemIcon,
  getWorkbook,
  translateBreadcrumb,
  translateMeilisearchBreadcrumb,
} from '@/utilities/homescreen.utilities';
import { getLockingChannel } from '@/utilities/lock.constants';
import { subscribe } from '@/utilities/socket.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { IconProps } from '@seeqdev/qomponents';

type Breadcrumb = {
  id: string;
  name: string;
  translationKey?: string;
};

interface NameCellProps {
  item: any;
  showBreadcrumbs: boolean;
  extraClassNames?: string;
  isCurrentWorkbook?: boolean;
}

export const NameCell = ({ item, showBreadcrumbs, extraClassNames, isCurrentWorkbook = false }: NameCellProps) => {
  const { t } = useTranslation();

  const [journalReportName, setJournalReportName] = useState<string>();
  const [journalReportContent, setJournalReportContent] = useState<string>();
  const isJournalReport = _.includes(JOURNAL_OR_REPORT, item.type);
  const searchText = useHomeScreenSearchText();
  const isAllowedLanguageCharacter = (word: string): boolean => /^[^-_/.,')'\\p{Alnum} ]+/.test(word);
  const isFoundInSearchText = (word: string): boolean => {
    if (!searchText) {
      return false;
    }

    const needle = _.toLower(searchText);
    const haystack = _.toLower(word);
    return (_.includes(needle, haystack) && isAllowedLanguageCharacter(word)) || _.includes(haystack, needle);
  };

  useEffect(() => {
    if (isJournalReport) {
      getWorkbook(item.workbookId).then((workbook) => {
        const itemWorksheet = _.find(workbook?.worksheets, { worksheetId: item.worksheetId });
        if (itemWorksheet) {
          setJournalReportName(`${workbook.name} - ${itemWorksheet.name}`);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (item.name !== t('UNNAMED') && item.name !== UNNAMED_REPORT && isJournalReport) {
      formatJournalReportContent(searchText, item.id).then((content) => setJournalReportContent(content));
    }
  }, [searchText, item]);

  const renderBreadcrumbs = (crumbs: string[] | Breadcrumb[] = []) => (
    <span data-testid="itemBreadcrumb" className="xsmall">
      {isNewHomeScreenWithMeilisearchEnabled()
        ? (crumbs as string[]).map((crumb) => translateMeilisearchBreadcrumb(crumb)).join(' » ')
        : (crumbs as Breadcrumb[]).map((crumb) => translateBreadcrumb(crumb)).join(' » ')}
    </span>
  );

  const renderJournalReportSearchResults = (content: string) => (
    <span className="small">
      {_.map(_.split(content, /[, ]+/), (word, index) => {
        return (
          <span key={`${word}-${index}`} className={isFoundInSearchText(word) ? 'tw:font-bold' : ''}>
            <ContainerWithHTML content={`${word} `} />
          </span>
        );
      })}
    </span>
  );

  const [localLocked, setLocalLocked] = useState(false);
  useEffect(() => {
    let unsubscribe: () => void | undefined;
    if (item.type === SeeqNames.Types.Analysis && !item.locked) {
      unsubscribe = subscribe({
        channelId: getLockingChannel(item.id),
        onMessage: () => {
          setLocalLocked(true);
        },
      });
    }
    return () => {
      unsubscribe?.();
    };
  }, []);

  const getCrumbs = () => {
    if (isNewHomeScreenWithMeilisearchEnabled()) {
      return Array.isArray(item.breadcrumbPath) ? item.breadcrumbPath : [];
    }

    return Array.isArray(item.ancestors) ? item.ancestors : [];
  };

  return (
    <div className={clsx('tw:flex tw:min-w-0 tw:flex-col', extraClassNames)}>
      {item.type === ITEM_TYPES.CAPTURE ? (
        <CaptureName name={item.name} captureNumber={item.captureNumber} workbookName={item.workbookName} />
      ) : item.type === ITEM_TYPES.CONTEXT_COMMENT ? (
        <>
          <span data-testid="itemName" className="xsmall">
            {item.name}
          </span>
          <span
            data-testid="itemDescription"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '400px',
              display: 'block',
            }}>
            {item.description}
          </span>
        </>
      ) : (
        <>
          {showBreadcrumbs && renderBreadcrumbs(getCrumbs())}
          <span data-testid="itemName" className="tw:flex tw:min-w-0">
            <span className="tw:min-w-0 tw:wrap-anywhere">{journalReportName ?? item.name}</span>
            {journalReportName == null && (item.locked || localLocked) ? (
              <Icon icon={faLock} extraClassNames="tw:m-sq-5 tw:mt-sq-3 tw:shrink-0" testId="lockedAnalysis" />
            ) : null}
          </span>
          {isJournalReport && !_.isNil(journalReportContent) && (
            <span data-testid="journalReportPreview">{renderJournalReportSearchResults(journalReportContent)}</span>
          )}
        </>
      )}
      {isCurrentWorkbook && <span className="xsmall">({t('EXPLORER_MODAL.CURRENT_WORKBOOK')})</span>}
    </div>
  );
};

type CaptureNameProps = {
  name: string;
  captureNumber?: string;
  workbookName?: string;
};

/** The name cell of a capture search result: its title over its number and source workbook. */
const CaptureName = ({ name, captureNumber, workbookName }: CaptureNameProps) => (
  <>
    <span data-testid="itemName">{name}</span>
    <span data-testid="captureSource" className="tw:text-xs">
      {[captureNumber, workbookName].filter(Boolean).join(' · ')}
    </span>
  </>
);

interface HomeScreenIconCellProps {
  item: any;
  spinning?: boolean;
}

export const HomeScreenItemIconCell = ({ item, spinning = false }: HomeScreenIconCellProps) => {
  const itemIcon = getItemIcon(item);

  return (
    <div data-testid={`icon_${item.id}`}>
      <span
        className={clsx('tw:flex tw:h-sq-26 tw:w-sq-26 tw:items-center tw:justify-center tw:rounded-sq-5 tw:text-xs', {
          'workbenchDisplay': item.type !== ITEM_TYPES.CONTEXT_COMMENT,
          'workbenchAnalysis': item.type === ITEM_TYPES.JOURNAL,
          'workbenchReport': item.type === ITEM_TYPES.REPORT,
          'actionBoard': item.type === ITEM_TYPES.ACTION_BOARD,
          'sq-text-vantage': item.type === ITEM_TYPES.CONTEXT_COMMENT,
        })}>
        <IconWithSpinner
          spinning={spinning}
          icon={itemIcon}
          type="inherit"
          extraClassNames={item.type === ITEM_TYPES.FOLDER && !spinning ? 'tw:relative tw:left-px' : undefined}
        />
      </span>
    </div>
  );
};

interface DateCellProps {
  item: any;
  accessor: string;
}

export const DateCell = ({ item, accessor }: DateCellProps) => {
  const { use24HourFormat } = useDateTimeFormatPreferences();
  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);

  if (!item[accessor]) {
    return null;
  }

  const format = getPreferredTimeFormat(use24HourFormat);
  return <span>{formatTime(item[accessor], timezone, format)}</span>;
};

export const ProgressCell = ({ percentage }: { percentage: number }) => (
  <div className="flexColumnContainer tw:items-center tw:justify-center">
    <span className="dataStatusIcon tw:align-middle">
      <CircularProgressbar value={percentage} text={_.toString(percentage)} />
    </span>
  </div>
);

export const IconCell = (props: IconProps) => (
  <div className="flexColumnContainer tw:items-center tw:justify-center">
    <Icon {...props} />
  </div>
);
